/**
 * タスクの読み書きを画面へ渡すための入れ物。
 *
 * API との通信・読み込み中・失敗の扱いをここに閉じ込め、画面は「一覧」「作る」「直す」
 * 「消す」だけを見る。失敗したときは画面に理由を出し、勝手にサンプルへ切り替えない
 * （繋がっていないことに気づけなくなるため）。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business チームごとのタスクを API から読み、作成・更新・削除を画面へ提供する。
 * @graph-connects fetchTasks [calls] タスクを引く
 * @graph-connects createTask [calls] タスクを作る
 * @graph-connects updateTask [calls] タスクを直す
 * @graph-connects deleteTask [calls] タスクを消す
 */
import { toMessage } from './to-message.js';
import { useCallback, useEffect, useState } from 'react';
import type { Task, TeamId } from '@/tasks/task';
import { createTask, deleteTask, fetchTasks, updateTask } from './client.js';

/**
 * 開いている間に読み直す間隔。
 *
 * 他の人の編集を、リロードしなくても 1 分以内に映すため。リアルタイム同期（Firestore を
 * 画面から直接購読）は認証と権限を画面側にも持たせる必要があり、数人で使う規模には重い
 */
/** @graph-connects none */
export const AUTO_RELOAD_INTERVAL_MS = 60_000;

/** 画面へ渡すもの */
export interface TasksState {
  /** いま表示するタスク */
  tasks: readonly Task[];
  /** 読み込み中か */
  loading: boolean;
  /** 失敗したときの理由。無ければ空文字 */
  error: string;
  /** 読み直す */
  reload: () => void;
  /** まとめて作る（月次経理からの作成で使う）。全部書けたときだけ true */
  createMany: (created: readonly Omit<Task, 'id'>[]) => Promise<boolean>;
  /** 1 件直す。書けたときだけ true */
  update: (task: Task) => Promise<boolean>;
  /** 1 件消す。消せたときだけ true */
  remove: (id: string) => Promise<boolean>;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business チームのタスクを読み込み、作成・更新・削除のたび・1 分ごと・画面に戻ったときに読み直す
 * @graph-connects fetchTasks [calls] タスクを引く
 */
export function useTasks(team: TeamId): TasksState {
  const [tasks, setTasks] = useState<readonly Task[]>([]);
  // すでに読めているチームかどうか。書き込みのたびに読み直すので、そのつど
  // 「読み込んでいます」を出すと画面の高さが動き、カレンダーで予定を動かすたびに
  // 揺れて見える。チームを切り替えたときは中身が入れ替わるので、そこでは出す
  const [loadedTeam, setLoadedTeam] = useState('');
  const [loading, setLoading] = useState(true);
  // 読み込みと書き込みで失敗の理由を分けて持つ。書き込みが失敗したあとは読み直すので、
  // 1 つにまとめると読み直しの成功で「書けなかった」という理由が消えてしまう
  const [loadError, setLoadError] = useState('');
  const [writeError, setWriteError] = useState('');
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError('');

    fetchTasks(team)
      .then((loaded) => {
        if (alive) {
          setTasks(loaded);
        }
      })
      .catch((cause: unknown) => {
        if (alive) {
          setLoadError(toMessage(cause));
        }
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
          setLoadedTeam(team);
        }
      });

    return () => {
      // 表示するチームが変わったあとに古い応答で上書きしない
      alive = false;
    };
  }, [team, reloadCount]);

  // 他の人の編集を取り込むため、1 分ごとと、画面に戻ってきたときに読み直す。
  // 裏に回っているタブでは読まない（誰も見ていないのに API を叩き続けないため）。
  // 開いているダイアログやドラッグ中の予定は画面側の状態で持っているので、
  // 読み直しても編集中の中身は消えない
  useEffect(() => {
    const reloadIfVisible = (): void => {
      if (document.visibilityState === 'visible') {
        setReloadCount((count) => count + 1);
      }
    };
    const timer = window.setInterval(reloadIfVisible, AUTO_RELOAD_INTERVAL_MS);
    document.addEventListener('visibilitychange', reloadIfVisible);
    window.addEventListener('focus', reloadIfVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', reloadIfVisible);
      window.removeEventListener('focus', reloadIfVisible);
    };
  }, []);

  const reload = useCallback(() => {
    setReloadCount((count) => count + 1);
  }, []);

  /**
   * 書き込みのたびに読み直す。手元だけ書き換えると、他の人の変更とずれる。
   *
   * 失敗したときも読み直す。まとめて作る途中で失敗すると、書けたぶんと書けなかった
   * ぶんが混ざるため、手元を古いままにすると次の操作が実態とずれる。
   * 書けたかどうかは呼び出し元へ返す（握りつぶすと成功したように見えてしまう）
   */
  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setWriteError('');
    try {
      await action();
      setReloadCount((count) => count + 1);
      return true;
    } catch (cause: unknown) {
      setWriteError(toMessage(cause));
      setReloadCount((count) => count + 1);
      return false;
    }
  }, []);

  return {
    tasks,
    loading: loading && loadedTeam !== team,
    // 書けなかった理由を優先する。読み直しは成功しても、書けていないことに変わりはない
    error: writeError === '' ? loadError : writeError,
    reload,
    createMany: (created) =>
      run(async () => {
        for (const task of created) {
          await createTask(task);
        }
      }),
    update: (task) =>
      run(async () => {
        const { id, ...patch } = task;
        await updateTask(id, patch);
      }),
    remove: (id) => run(() => deleteTask(id)),
  };
}
